/**
 * One-shot capture of the host page as a PNG, taken from the host page
 * itself so the user's click on the "Screenshot this page" bar carries the
 * user activation `getDisplayMedia` requires.
 *
 * The widget has its own copy of this frame-grab logic in
 * `-capture-screenshot.ts`. The snippet cannot share it: it ships as a
 * dependency-free CDN bundle and a `MediaStream` cannot cross the iframe
 * boundary, so the frame has to be grabbed where the stream was opened.
 *
 * The share hint asks the browser to offer the current tab first
 * (`preferCurrentTab`): on Chromium the picker collapses to a single confirm
 * for this tab, which is what "screenshot this page" means. Other browsers
 * ignore the hint and show their regular picker – the widget and the bar are
 * hidden at that point, so any choice still shows the page underneath.
 *
 * Resolves to `undefined` when the user dismissed the picker; any other
 * failure throws.
 */

// `getDisplayMedia` resolves while the picker is still fading out over the
// page. The first frames show that dialog, so wait for the compositor to
// deliver a clean one – Chromium's fade runs about 300 ms.
const PICKER_SETTLE_MS = 600;

type ImageCaptureCtor = new (track: MediaStreamTrack) => {
	grabFrame: () => Promise<ImageBitmap>;
};

const sleep = async (ms: number): Promise<void> => {
	// oxlint-disable-next-line promise/avoid-new -- no DOM helper exists for awaiting a timer
	await new Promise<void>((resolve) => {
		setTimeout(resolve, ms);
	});
};

const stopStream = (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- MediaStream is an external mutable DOM interface
	stream: MediaStream,
): void => {
	for (const track of stream.getTracks()) {
		track.stop();
	}
};

const drawToPng = async (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- CanvasImageSource is a union of external mutable DOM interfaces
	source: CanvasImageSource,
	width: number,
	height: number,
): Promise<Blob> => {
	const canvas = new OffscreenCanvas(width, height);
	const context = canvas.getContext("2d");
	if (context === null) {
		throw new Error("2D context unavailable");
	}
	context.drawImage(source, 0, 0, width, height);
	return await canvas.convertToBlob({ type: "image/png" });
};

const waitForVideoData = async (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- HTMLVideoElement is an external mutable DOM interface
	video: HTMLVideoElement,
): Promise<void> => {
	if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
		return;
	}
	// oxlint-disable-next-line promise/avoid-new -- no DOM helper exists for awaiting a single media event
	await new Promise<void>((resolve, reject) => {
		video.addEventListener(
			"loadeddata",
			() => {
				resolve();
			},
			{ once: true },
		);
		video.addEventListener(
			"error",
			() => {
				reject(new Error("Video failed to load"));
			},
			{ once: true },
		);
	});
};

// Fallback for browsers without `ImageCapture`: play the stream into an
// off-DOM video element and paint its first frame.
const videoFrameToPng = async (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- MediaStream is an external mutable DOM interface
	stream: MediaStream,
): Promise<Blob> => {
	const video = document.createElement("video");
	video.srcObject = stream;
	video.muted = true;
	video.playsInline = true;
	await video.play();
	await waitForVideoData(video);
	const blob = await drawToPng(video, video.videoWidth, video.videoHeight);
	video.srcObject = null;
	return blob;
};

const grabFrame = async (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- MediaStream is an external mutable DOM interface
	stream: MediaStream,
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- MediaStreamTrack is an external mutable DOM interface
	track: MediaStreamTrack,
): Promise<Blob> => {
	if ("ImageCapture" in globalThis) {
		// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- narrowing globalThis to a known-shape interop view
		const ambient = globalThis as unknown as { ImageCapture: ImageCaptureCtor };
		const bitmap = await new ambient.ImageCapture(track).grabFrame();
		try {
			return await drawToPng(bitmap, bitmap.width, bitmap.height);
		} finally {
			bitmap.close();
		}
	}
	return await videoFrameToPng(stream);
};

/** Whether this browser can open a screen-share picker at all. */
export const canCapturePage = (): boolean =>
	typeof navigator !== "undefined" &&
	typeof navigator.mediaDevices?.getDisplayMedia === "function";

export const capturePage = async (): Promise<Uint8Array | undefined> => {
	let stream: MediaStream;
	try {
		stream = await navigator.mediaDevices.getDisplayMedia({
			audio: false,
			// Chromium-only hints; other browsers ignore unknown members.
			// oxlint-disable typescript-eslint/no-unsafe-type-assertion -- the hint members are newer than the bundled lib.dom
			...({
				monitorTypeSurfaces: "exclude",
				preferCurrentTab: true,
				selfBrowserSurface: "include",
				surfaceSwitching: "exclude",
			} as DisplayMediaStreamOptions),
			// oxlint-enable typescript-eslint/no-unsafe-type-assertion
			video: true,
		});
	} catch (error) {
		if (error instanceof DOMException && error.name === "NotAllowedError") {
			return undefined;
		}
		throw error;
	}
	const [track] = stream.getVideoTracks();
	if (track === undefined) {
		stopStream(stream);
		throw new Error("No video track");
	}
	try {
		await sleep(PICKER_SETTLE_MS);
		const blob = await grabFrame(stream, track);
		return new Uint8Array(await blob.arrayBuffer());
	} finally {
		// Stop right away so the browser's "sharing your screen" indicator goes
		// away the moment the frame is in hand.
		stopStream(stream);
	}
};
